import { useAppSelector } from "@/app/hooks";
import { MediaType, useGetPropertyMediaQuery } from "@/services/mediaService";
import useTransformedImageUrl from "@/shared/hooks/useTransformedImage";
import { EmeraldIcon } from "@emerald-react/icon";
import { useState } from "react";
import { useParams } from "react-router";
import ImageGridItem from "./ImageGridItem";
import ImageModal from "./ImageModal";

const PropertyImages = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Get the propertyId from the URL parameters
  const { surveyId, propertyId } = useParams<{
    surveyId: string;
    propertyId: string;
  }>();

  const { currentSurvey } = useAppSelector((state) => state.interactiveSurvey);

  // Fetch property images
  const { currentData: propertyImages, isFetching: isLoading } =
    useGetPropertyMediaQuery(
      {
        surveyId: surveyId!,
        propertyId: propertyId!,
        mediaType: MediaType.IMAGES,
      },
      { skip: !surveyId || !propertyId || !currentSurvey },
    );

  // Get the main image (first image, or find one with order=true if that indicates main image)
  const mainImage =
    propertyImages?.find((image) => image.order === true) ||
    propertyImages?.[0];

  // Transform main image URL
  const { transformedImageUrl: transformedMainImageUrl } =
    useTransformedImageUrl(mainImage?.cloudfrontUrl);

  // Get remaining images for the grid (excluding the main image)
  const remainingImages = propertyImages?.filter(
    (image) => image.mediaId !== mainImage?.mediaId,
  );

  // Check if photos should be included
  const shouldShowPhotoGrid =
    currentSurvey?.isIncludePhotos &&
    remainingImages &&
    remainingImages.length > 0;

  // Combine all images for the modal (main image + remaining images)
  const allImages = propertyImages || [];

  // Modal handlers
  const openModal = (index: number) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : allImages.length - 1,
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) =>
      prev < allImages.length - 1 ? prev + 1 : 0,
    );
  };

  return (
    <div className="w-full bg-white">
      <div className="pb-2 lg:pb-24 lg:min-h-full">
        {/* Main Image Area */}
        <div
          className="flex items-center justify-center bg-gray-100 h-[220px] lg:h-[590px] bg-cover bg-center bg-no-repeat cursor-pointer"
          style={
            mainImage && !isLoading
              ? {
                  backgroundImage: `url(${transformedMainImageUrl})`,
                }
              : undefined
          }
          onClick={() => mainImage && openModal(0)}
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <div className="mb-4">Loading...</div>
            </div>
          ) : !mainImage ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <div className="mb-4">
                <EmeraldIcon
                  icon="business"
                  type="filled"
                  variant="icon"
                  className="emerald-icon-responsive"
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* Additional Photos Grid */}
        {shouldShowPhotoGrid && (
          <div className="bg-white">
            <div className="py-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-1">
                {remainingImages?.map((image, index) => (
                  <ImageGridItem
                    key={image.mediaId}
                    mediaId={image.mediaId}
                    cloudfrontUrl={image.cloudfrontUrl}
                    onClick={() => openModal(index + 1)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={isModalOpen}
        images={allImages}
        currentIndex={currentImageIndex}
        onClose={closeModal}
        onNext={goToNext}
        onPrevious={goToPrevious}
        onSelectImage={setCurrentImageIndex}
      />
    </div>
  );
};

export default PropertyImages;
